/**
 * Types for the per-transformer CloudWatch health alarms shown on the Edge
 * Compute tab (M1 part C). Produced by `MgmtCellClientManager` and consumed by
 * `useEdgeComputeStatus` / `EdgeComputeTab`.
 */

/**
 * The five per-transformer CloudWatch alarm-name suffixes. The alarm name for a
 * host is `Transformer{N}{suffix}` where N is the numeric part of the server
 * name (e.g. `transformer-3` -> `3`). Mirrors AwsGsaasTransformerOpsCli's
 * TRANSFORMER_ALARM_SUFFIXES so the GSOC page shows the same alarm set the
 * operator CLI's `list-transformers --details` surfaces.
 */
export const TRANSFORMER_ALARM_SUFFIXES = [
  'InconsistentAssignedStatusAlarm',
  'GroundStationAgentNoPulseAlarm',
  'HealthCheckFailureAssignedHostAlarm',
  'HealthCheckFailureAlarm',
  'SsmHealthCheckMissingDataAlarm',
] as const;

/** One per-transformer alarm and its current CloudWatch state. */
export interface TransformerAlarm {
  /** The alarm-name suffix (e.g. `InconsistentAssignedStatusAlarm`). */
  readonly suffix: string;
  /** CloudWatch alarm state: `OK` / `ALARM` / `INSUFFICIENT_DATA`, or `MISSING` when the alarm does not exist. */
  readonly state: string;
}

/** One step in an alarm's state timeline: the state that began at `x`. */
export interface AlarmStatePoint {
  readonly x: Date;
  /** `OK` / `ALARM` / `INSUFFICIENT_DATA`. */
  readonly state: string;
}

/**
 * The last hour of an alarm's STATE timeline (from CloudWatch
 * `DescribeAlarmHistory`), for the plot the operator opens by expanding an alarm.
 * `points` is the ordered list of state changes over the window (oldest-first),
 * seeded with the state in effect at the window start so the line covers the full
 * hour. This is the actual OK / ALARM / INSUFFICIENT_DATA the alarm evaluated to,
 * not the underlying metric.
 */
export interface AlarmSeries {
  readonly points: AlarmStatePoint[];
}
